import { BACKEND_URL } from '../config/backendUrl';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import BullhornNotePromptDialog from '../components/BullhornNotePromptDialog';
import { useBullhornNoteHandler } from '../hooks/useBullhornNoteHandler';
import { PlacementChip } from '../components/send/PlacementChip';
import { WizardSteps } from '../components/send/WizardSteps';

const API_URL = `${BACKEND_URL}/api`;
import { templatesApi, bullhornApi, documentsApi, settingsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { formatEstimate } from '../utils/duration';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import SignaturePad from 'signature_pad';
import { toast } from 'sonner';
import { templateTypeLabel, templateTypeTagClass } from '../utils/templateType';
import { useLinkExpiry, linkExpiryOptions } from '../utils/linkExpiry';
import { Document, Page, pdfjs } from 'react-pdf';
import TextTemplateFillIn from '../components/send/TextTemplateFillIn';
// Stable ref so react-pdf sends the auth cookie on cross-origin (Pages→Worker)
// PDF fetches, without reloading on every render.
const PDF_HTTP_OPTIONS = { withCredentials: true };
import { 
  Send, 
  Search, 
  User,
  FileText,
  Clock,
  RefreshCw,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Edit3,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Plus,
  Trash2,
  Type,
  Calendar,
  CheckSquare,
  List,
  PenTool,
  Mail,
  Phone,
  Hash,
  GripVertical,
  Link2,
  Lock,
  Unlock,
  ShieldCheck,
  ArrowUpDown,
  Database,
  Building2,
  Briefcase,
  UserCheck,
  Upload,
  Circle,
  Eye,
  X
} from 'lucide-react';
import { isMoneyField } from '../utils/moneyFormatting';

// PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Steps will be customized based on entity type
const getSteps = (entityType) => {
  const recipientTitle = entityType === 'contact' ? 'Choose Contact'
    : entityType === 'placement' ? 'Choose Placement'
    : 'Choose Candidate';
  const recipientIcon = entityType === 'contact' ? Building2
    : entityType === 'placement' ? Briefcase
    : User;
  return [
    { id: 'template', title: 'Select Template', icon: FileText },
    { id: 'recipient', title: recipientTitle, icon: recipientIcon },
    { id: 'edit', title: 'Customize Document', icon: Edit3 },
    { id: 'review', title: 'Review & Send', icon: Send },
  ];
};

// Field type definitions
const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare },
  { value: 'radio_group', label: 'Radio Group', icon: Circle },
  { value: 'dropdown', label: 'Dropdown', icon: List },
  { value: 'signature', label: 'Signature', icon: PenTool },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'file_upload', label: 'File Upload', icon: Upload },
];

// Keep this palette aligned with TemplateEditorPage so a template looks the
// same while editing and while being customized for a single send.
const FIELD_COLORS = {
  text: 'bg-blue-50/80 border-blue-300',
  email: 'bg-cyan-50/80 border-cyan-300',
  phone: 'bg-teal-50/80 border-teal-300',
  number: 'bg-indigo-50/80 border-indigo-300',
  date: 'bg-purple-50/80 border-purple-300',
  checkbox: 'bg-green-50/80 border-green-300',
  radio_group: 'bg-lime-50/80 border-lime-300',
  dropdown: 'bg-orange-50/80 border-orange-300',
  signature: 'bg-pink-50/80 border-pink-300',
};

// Keep Bullhorn's stable picklist IDs in fieldValues for write-back, while
// showing recruiters the corresponding human-readable labels.
const getFieldDisplayValue = (field, rawValue) => {
  if (rawValue == null || rawValue === '') return '';
  const options = field?.bullhorn_picklist_options || [];
  if (!options.length) return String(rawValue);

  const labelsByValue = new Map(
    options.map((option) => [
      String(option?.value ?? '').trim().toLowerCase(),
      String(option?.label ?? option?.value ?? '').trim(),
    ])
  );
  const values = Array.isArray(rawValue) ? rawValue : String(rawValue).split(',');
  return values
    .map((value) => {
      const normalized = String(value ?? '').trim();
      return labelsByValue.get(normalized.toLowerCase()) || normalized;
    })
    .filter(Boolean)
    .join(', ');
};

// Recruiter canvas preview only. Keep fieldValues raw so editing and Bullhorn
// submission never receive display punctuation or currency symbols.
const getFieldPreviewValue = (field, rawValue) => {
  const resolved = getFieldDisplayValue(field, rawValue);
  if (resolved === '') return '';
  if (field?.field_type !== 'number') return resolved;
  const symbol = field.currency_symbol || '';
  const formatMoney = isMoneyField(field);
  if (!formatMoney) return resolved;
  const number = Number(String(resolved).replace(/,/g, '').replace(symbol, '').trim());
  if (!Number.isFinite(number)) return resolved;
  const formatted = number.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (!symbol) return formatted;
  return (field.currency_position || 'prefix') === 'suffix' ? `${formatted}${symbol}` : `${symbol}${formatted}`;
};

const applyPayeAssignmentCompatibility = (templateName, fields) => {
  if (!/(?=.*\bassignment schedule\b)(?=.*\bpaye\b)/i.test(String(templateName || ''))) return fields;
  return fields.map((field) => {
    const label = String(field.label || '').trim();
    const updates = {};
    const mapped = field.mapped_bullhorn_field || field.bullhorn_source_field || '';
    if (
      /^(company|client\s*\/\s*hiring company)$/i.test(label)
      && (!mapped || /^(clientCorporation|company)$/i.test(mapped))
    ) {
      updates.mapped_bullhorn_field = 'company.name';
    }
    if (/^pay rate(?:\s+\d+)?$/i.test(label)) {
      updates.field_type = 'number';
      updates.mapped_bullhorn_field = mapped || 'placement.payRate';
      updates.currency_symbol = field.currency_symbol || '£';
      updates.currency_position = 'prefix';
      updates.currency_format_money = true;
    }
    return Object.keys(updates).length ? { ...field, ...updates } : field;
  });
};

// Bullhorn entity colors (from Bullhorn official brand guidelines)
const ENTITY_COLORS = {
  candidate: { bg: 'bg-[#4CAF50]', text: 'text-white', label: 'Candidate', lightBg: 'bg-[#4CAF50]/10', border: 'border-[#4CAF50]', iconColor: 'text-[#4CAF50]' },  // Green
  contact: { bg: 'bg-[#F9A825]', text: 'text-white', label: 'Contact', lightBg: 'bg-[#F9A825]/10', border: 'border-[#F9A825]', iconColor: 'text-[#F9A825]' },  // Yellow/Orange
  placement: { bg: 'bg-[#1a4a5e]', text: 'text-white', label: 'Placement', lightBg: 'bg-[#1a4a5e]/10', border: 'border-[#1a4a5e]', iconColor: 'text-[#1a4a5e]' },  // Deep Teal/Navy (Bullhorn)
};

// Bullhorn field mapping options - organized by entity type
const BULLHORN_CANDIDATE_FIELDS = [
  { value: 'none', label: 'None (Manual Entry)' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'name', label: 'Full Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'address.address1', label: 'Address Line 1' },
  { value: 'address.address2', label: 'Address Line 2' },
  { value: 'address.city', label: 'City' },
  { value: 'address.state', label: 'State/County' },
  { value: 'address.zip', label: 'Zip/Postcode' },
  { value: 'address.countryID', label: 'Country' },
  { value: 'dateOfBirth', label: 'Date of Birth' },
  { value: 'ssn', label: 'SSN / NI Number' },
  { value: 'salary', label: 'Salary' },
  { value: 'payRate', label: 'Pay Rate' },
  { value: 'employeeType', label: 'Employee Type' },
  { value: 'status', label: 'Status' },
  { value: 'customText1', label: 'Custom Text 1' },
  { value: 'customText2', label: 'Custom Text 2' },
  { value: 'customText3', label: 'Custom Text 3' },
];

const BULLHORN_CONTACT_FIELDS = [
  { value: 'none', label: 'None (Manual Entry)' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'name', label: 'Full Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'title', label: 'Job Title' },
  { value: 'address.address1', label: 'Address Line 1' },
  { value: 'address.city', label: 'City' },
  { value: 'address.state', label: 'State/County' },
  { value: 'address.zip', label: 'Zip/Postcode' },
  { value: 'address.countryID', label: 'Country' },
  { value: 'customText1', label: 'Custom Text 1' },
  { value: 'customText2', label: 'Custom Text 2' },
  { value: 'customText3', label: 'Custom Text 3' },
];

const BULLHORN_COMPANY_FIELDS = [
  { value: 'company.name', label: 'Company Name' },
  { value: 'company.phone', label: 'Company Phone' },
  { value: 'company.address.address1', label: 'Company Address' },
  { value: 'company.address.city', label: 'Company City' },
  { value: 'company.address.state', label: 'Company State' },
  { value: 'company.address.zip', label: 'Company Zip' },
  { value: 'company.customText1', label: 'Company Custom 1' },
  { value: 'company.customText2', label: 'Company Custom 2' },
];

// Draggable Field Component for Send Document flow
const DraggableFieldMini = ({ field, isSelected, scale, onSelect, onUpdate, onDelete, fieldValue }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialSize, setInitialSize] = useState({ width: 0, height: 0 });

  const handleMouseDown = (e) => {
    if (e.target.dataset.resize) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - field.position_x * scale,
      y: e.clientY - field.position_y * scale,
    });
    onSelect(field.id);
  };

  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialSize({ width: field.width, height: field.height });
    onSelect(field.id);
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      const newX = Math.max(0, (e.clientX - dragStart.x) / scale);
      const newY = Math.max(0, (e.clientY - dragStart.y) / scale);
      onUpdate(field.id, { position_x: newX, position_y: newY });
    } else if (isResizing) {
      const deltaX = (e.clientX - dragStart.x) / scale;
      const deltaY = (e.clientY - dragStart.y) / scale;
      onUpdate(field.id, {
        width: Math.max(30, initialSize.width + deltaX),
        height: Math.max(15, initialSize.height + deltaY)
      });
    }
  }, [isDragging, isResizing, dragStart, scale, field.id, onUpdate, initialSize]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  // Determine what to show in the field. Coerce to string — Bullhorn prefill values
  // can be numbers, and .trim() on a number crashes the render.
  const displayValue = fieldValue == null ? '' : String(fieldValue);
  const hasValue = displayValue.trim() !== '';
  const isLocked = field.candidate_editable === false;

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onSelect(field.id); }}
      onMouseDown={handleMouseDown}
      className={`absolute border-2 rounded-sm cursor-move select-none transition-shadow ${
        isSelected ? 'ring-2 ring-[#f97316] ring-offset-1 shadow-lg z-20 border-[#f97316]' : 'hover:shadow z-10 hover:border-[#f97316]/50'
      } ${isDragging ? 'cursor-grabbing opacity-80' : ''} ${
        isLocked
          ? 'bg-amber-50 border-amber-400'
          : field.transparent_background && !isSelected
          ? 'bg-transparent border-dashed border-gray-300/70'
          : FIELD_COLORS[field.field_type] || 'bg-gray-50 border-gray-300'
      }`}
      style={{
        left: field.position_x * scale,
        top: field.position_y * scale,
        width: field.width * scale,
        height: field.height * scale,
      }}
      data-testid={`send-field-${field.id}`}
      title={`${field.label}${hasValue ? ': ' + displayValue : ''}${isLocked ? ' (Locked - Read Only)' : ''}`}
    >
      <div className="absolute inset-0 flex items-center overflow-hidden pointer-events-none px-1">
        {isLocked && <Lock className="w-3 h-3 text-amber-600 mr-1 flex-shrink-0" />}
        {hasValue ? (
          <span className="text-[9px] font-medium text-gray-800 truncate">
            {displayValue}
          </span>
        ) : (
          <span className="text-[8px] font-medium text-gray-500 truncate opacity-70">
            {field.label.length > 12 ? field.label.substring(0, 10) + '..' : field.label}
          </span>
        )}
      </div>
      {/* Status indicators */}
      {isLocked ? (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full flex items-center justify-center" title="Locked">
          <Lock className="w-2 h-2 text-white" />
        </div>
      ) : field.required && !hasValue ? (
        <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
      ) : hasValue ? (
        <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full" title="Pre-filled" />
      ) : null}
      {isSelected && (
        <>
          <div
            data-resize="se"
            onMouseDown={handleResizeStart}
            className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-[#f97316] rounded-full cursor-se-resize border border-white shadow-sm"
          />
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(field.id); }}
            className="absolute -top-3 -right-3 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
          >
            <Trash2 className="w-3 h-3 text-white" />
          </button>
        </>
      )}
    </div>
  );
};

// Where a placement template actually delivers — mirrors the worker's own
// routing (packets.ts targetsFor / documents.ts createAndSend's guard): a
// candidate/contact-typed template routes by its own entity_type; a
// placement-typed template routes by its send_to_candidate/send_to_contact
// flags. send_to_candidate/send_to_contact arrive from the API as 0/1, so
// this must coerce with `!!`, not compare against the literal `false`.
function templateAllowsSide(t, side) {
  if (!t) return true;
  const entity = (t.entity_type || 'candidate').toLowerCase();
  if (entity !== 'placement') return side === 'contact' ? entity === 'contact' : entity !== 'contact';
  return side === 'contact' ? !!t.send_to_contact : !!t.send_to_candidate;
}

export default function SendDocumentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, getTenantPath } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Form data
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sendMode, setSendMode] = useState('template'); // 'template' or 'quick'
  const [quickSendFile, setQuickSendFile] = useState(null);
  const [quickSendName, setQuickSendName] = useState('');
  const [recipientPreFilled, setRecipientPreFilled] = useState(false);
  const [entityType, setEntityType] = useState('candidate');
  const [candidate, setCandidate] = useState({
    bullhorn_id: '',
    name: '',
    email: '',
    // Extended Bullhorn fields
    firstName: '',
    lastName: '',
    phone: '',
    mobile: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    dateOfBirth: '',
    ssn: '',
    title: '',  // For contacts
  });
  // Company data for Contact templates
  const [company, setCompany] = useState({
    bullhorn_id: '',
    name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
  });
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [companySearchResults, setCompanySearchResults] = useState([]);
  const [searchingCompany, setSearchingCompany] = useState(false);
  const [showCompanyOverride, setShowCompanyOverride] = useState(false);
  
  const { expiryHours, setExpiryHours } = useLinkExpiry(settingsApi.getLinkExpiry);
  const [message, setMessage] = useState('');

  // Multi-placement send (placement entity type only). When non-empty, the
  // currently displayed `candidate` slot is treated as the "active/primary"
  // placement; on Send we loop over every entry in this queue and fire one
  // document-send per placement. Kept here rather than in `candidate` itself
  // so the existing field-prefill logic (which depends on the single active
  // candidate) continues to work untouched.
  const [selectedPlacements, setSelectedPlacements] = useState([]);

  // Post-send Bullhorn note. The hook reads tenant settings to decide
  // whether to popup / toast / silent / off. `onComplete` runs after
  // the popup closes OR immediately for non-popup modes — used to
  // navigate the recruiter onward.
  const bullhornNote = useBullhornNoteHandler();
  
  // Document field editing state (full editor)
  const [documentFields, setDocumentFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  // Text templates have no PDF to customize: the Customize step is a fill-in form
  // that reports whether every field now has a value (see TextTemplateFillIn).
  const [textStatus, setTextStatus] = useState({ loading: true, ready: false, blank: [] });

  // Sender/admin signing — for a Requester/Admin field of type "signature",
  // opened from the Pre-fill Values panel. Mirrors CandidateCompletionPage's
  // signature capture so the resulting image is in the same format.
  const [senderSignatureFieldId, setSenderSignatureFieldId] = useState(null);
  const [senderSignatureMode, setSenderSignatureMode] = useState('draw');
  const [senderTypedSignature, setSenderTypedSignature] = useState('');
  const senderSignaturePadRef = useRef(null);

  // Callback ref (not a useEffect) — this Dialog renders via a Radix portal,
  // so a plain useEffect keyed on state can fire before the canvas is
  // actually attached to the DOM, leaving SignaturePad uninitialized and
  // drawing silently doing nothing. A callback ref fires exactly when the
  // node mounts/unmounts, so it can't miss the timing.
  const initSenderSignatureCanvas = useCallback((canvasEl) => {
    senderSignaturePadRef.current = canvasEl
      ? new SignaturePad(canvasEl, { backgroundColor: 'rgb(255, 255, 255)', penColor: 'rgb(0, 0, 0)' })
      : null;
  }, []);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(0.75);
  const [pdfInfo, setPdfInfo] = useState({ width: 612, height: 792 });
  const [draggedFieldType, setDraggedFieldType] = useState(null);
  const canvasRef = useRef(null);
  const pdfScrollRef = useRef(null);
  const hasAutoFitRef = useRef(false);

  // Auto-fit PDF width to the canvas scroll container on first load so
  // the document doesn't render at a tiny fixed scale inside the wider
  // customize panel. Triggers once when documentFields land (i.e. the
  // recruiter has reached the Customize step).
  useEffect(() => {
    if (!documentFields.length) return;
    if (hasAutoFitRef.current) return;
    // Wait one tick for the panel to mount/layout
    const t = setTimeout(() => {
      const container = pdfScrollRef.current;
      if (!container || !pdfInfo.width) return;
      const available = container.clientWidth - 24;
      if (available <= 0) return;
      setScale(Math.min(1.2, Math.max(0.5, available / pdfInfo.width)));
      hasAutoFitRef.current = true;
    }, 50);
    return () => clearTimeout(t);
  }, [documentFields.length, pdfInfo.width]);

  const fitToWidth = () => {
    const container = pdfScrollRef.current;
    if (!container || !pdfInfo.width) return;
    const available = container.clientWidth - 24;
    if (available <= 0) return;
    setScale(Math.min(1.2, Math.max(0.5, available / pdfInfo.width)));
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  // Pre-fill recipient from URL params (when sent from Recipients page)
  useEffect(() => {
    const name = searchParams.get('name');
    const email = searchParams.get('email');
    const type = searchParams.get('type');
    if (name || email) {
      setCandidate(prev => ({ 
        ...prev, 
        name: name || prev.name, 
        email: email || prev.email 
      }));
      if (type) setEntityType(type);
      // Show a badge that recipient is pre-selected
      setRecipientPreFilled(true);
    }
  }, [searchParams]);

  // Initialize document fields when template is selected (deep copy)
  useEffect(() => {
    if (selectedTemplate) {
      // Set entity type from template
      setEntityType(selectedTemplate.entity_type || 'candidate');
      
      if (selectedTemplate.fields && selectedTemplate.fields.length > 0) {
        // Deep copy fields so we can modify them without affecting the original template.
        // Two passes: first assign new IDs + remember the mapping, then rewrite any
        // cross-field references (e.g. `filter_by_field_id` on Skill fields that point
        // to a Category field) so dependent-dropdown wiring survives the ID rewrite.
        const idMap = {};
        const stamped = selectedTemplate.fields.map((f, index) => {
          const newId = `doc_${f.id || index}_${Date.now()}_${index}`;
          idMap[f.id] = newId;
          return { ...f, original_id: f.id || `idx_${index}`, id: newId };
        });
        const copiedFields = applyPayeAssignmentCompatibility(selectedTemplate.name, stamped.map((f) => (
          f.filter_by_field_id && idMap[f.filter_by_field_id]
            ? { ...f, filter_by_field_id: idMap[f.filter_by_field_id] }
            : f
        )));
        setDocumentFields(copiedFields);
        
        // Initialize field values
        const initialValues = {};
        copiedFields.forEach(field => {
          initialValues[field.id] = '';
        });
        setFieldValues(initialValues);
      } else {
        setDocumentFields([]);
        setFieldValues({});
      }
      
      // Reset recipient and company when switching templates — but ONLY
      // when the recipient wasn't pre-filled from URL params (e.g. coming
      // from the Recipients page via "Send Document"). In that case the
      // user already chose the recipient and shouldn't have to re-pick.
      if (!recipientPreFilled) {
        setCandidate({
          bullhorn_id: '', name: '', email: '',
          firstName: '', lastName: '', phone: '', mobile: '',
          address: '', city: '', state: '', zip: '',
          dateOfBirth: '', ssn: '', title: '',
        });
        setCompany({ bullhorn_id: '', name: '', phone: '', address: '', city: '', state: '', zip: '' });
        setSelectedPlacements([]);
        setSearchQuery('');
        setSearchResults([]);
      }
    }
  }, [selectedTemplate?.id, recipientPreFilled]);

  // Pre-fill fields from Bullhorn candidate/contact/company data
  useEffect(() => {
    if (!candidate.name && !candidate.firstName) return;
    if (documentFields.length === 0) return;
    
    const updatedValues = { ...fieldValues };
    
    // If we only have full name (manual entry), try to split it
    let firstName = candidate.firstName;
    let lastName = candidate.lastName;
    if (!firstName && !lastName && candidate.name) {
      const nameParts = candidate.name.trim().split(' ');
      firstName = nameParts[0] || '';
      lastName = nameParts.slice(1).join(' ') || '';
    }
    
    // Helper to get candidate/contact value by Bullhorn field path
    const getBullhornValue = (fieldPath) => {
      if (!fieldPath) return '';
      const path = fieldPath.toLowerCase();
      
      // Handle company fields
      if (path.startsWith('company.')) {
        const companyPath = path.replace('company.', '');
        switch (companyPath) {
          case 'name': return company.name;
          case 'phone': return company.phone;
          case 'address.address1': return company.address;
          case 'address.city': return company.city;
          case 'address.state': return company.state;
          case 'address.zip': return company.zip;
          default: return '';
        }
      }
      
      // Handle candidate/contact fields
      switch (path) {
        case 'firstname': return firstName;
        case 'lastname': return lastName;
        case 'name': return candidate.name;
        case 'email': return candidate.email;
        // Phone fields are returned strictly — no cross-fallback between
        // `phone` and `mobile`. The two values are independent Bullhorn
        // fields and falling back from one to the other clobbers distinct
        // form fields (e.g. Mobile Tel No filled with the home phone).
        case 'phone': return candidate.phone;
        case 'mobile': return candidate.mobile;
        case 'title': return candidate.title;
        case 'address.address1': return candidate.address;
        case 'address.city': return candidate.city;
        case 'address.state': return candidate.state;
        case 'address.zip': return candidate.zip;
        case 'dateofbirth': return candidate.dateOfBirth;
        case 'ssn': return candidate.ssn;
        default: return '';
      }
    };
    
    documentFields.forEach(field => {
      const labelLower = field.label.toLowerCase();
      
      // First priority: Use explicitly set bullhorn_source_field
      if (field.bullhorn_source_field) {
        const value = getBullhornValue(field.bullhorn_source_field);
        if (value) {
          updatedValues[field.id] = value;
        }
        // If getBullhornValue couldn't resolve the mapping (e.g. customText13,
        // a non-standard Bullhorn field this UI helper doesn't know about),
        // DO NOT fall through to label-pattern auto-match — the recruiter
        // explicitly mapped this field to a Bullhorn source, so leave it
        // blank and let the backend's full Bullhorn fetch (which uses the
        // real Bullhorn API) populate it. Falling through to label-match
        // would wrongly assign e.g. `candidate.phone` to every field whose
        // label contains "tel no" (Home Tel, Mobile Tel, Emergency Home Tel,
        // Emergency Mobile Tel) — overwriting the correct per-field values
        // from Bullhorn.
        return;
      }
      
      // Second priority: Use mapped_bullhorn_field (from template)
      if (field.mapped_bullhorn_field) {
        const value = getBullhornValue(field.mapped_bullhorn_field);
        if (value) {
          updatedValues[field.id] = value;
        }
        return;
      }
      
      // Third priority: Auto-match by label patterns (only for unmapped fields)
      let value = '';
      
      // First Name matching
      if (labelLower.includes('first name') || 
          labelLower === 'forename' || 
          labelLower.includes('forename')) {
        value = firstName;
      }
      // Last Name / Surname matching
      else if (labelLower.includes('last name') || 
               labelLower.includes('surname') || 
               labelLower === 'surename') {
        value = lastName;
      }
      // Full Name matching — keep this STRICT. We only auto-prefill when
      // the label is unambiguously about the *candidate's own* name. The
      // earlier loose `includes('name')` catch-all wrongly populated
      // unrelated fields like "Referee 1 Name", "Next of Kin", "Emergency
      // Contact Name", "Spouse Name" etc. with the candidate's full name
      // — visible to the candidate, embarrassing for the recruiter.
      //
      // Match policy:
      //   1. Label must be exactly one of the candidate-self-reference
      //      phrasings (after lowercasing + trim), OR
      //   2. Label starts with "candidate "/"applicant "/"your " AND
      //      ends with "name".
      //   3. AND the first word is NOT a third-party context word
      //      (referee, emergency, next, kin, spouse, etc.) which
      //      catches longer combos like "Referee 1 Full Name".
      else if ((() => {
        const labelTrim = labelLower.trim();
        const firstWord = (labelTrim.split(/\s+/)[0] || '');
        const thirdPartyContexts = new Set([
          'referee', 'reference', 'emergency', 'next', 'kin',
          'spouse', 'partner', 'guardian', 'parent', 'mother',
          'father', 'doctor', 'gp', 'nominee', 'beneficiary',
          'witness', 'employer', 'manager', 'supervisor',
          'colleague', 'friend', 'contact', 'company',
          'previous', 'former', 'school', 'university', 'college',
        ]);
        const exactCandidateNameLabels = new Set([
          'name', 'full name', 'fullname',
          'candidate name', 'applicant name', 'employee name',
          'your name', 'your full name',
        ]);
        if (thirdPartyContexts.has(firstWord)) return false;
        if (exactCandidateNameLabels.has(labelTrim)) return true;
        const startsWithSelfPrefix = (
          labelTrim.startsWith('candidate ') ||
          labelTrim.startsWith('applicant ') ||
          labelTrim.startsWith('your ')
        );
        return startsWithSelfPrefix && labelTrim.endsWith('name');
      })()) {
        value = candidate.name;
      }
      // Email matching
      else if (labelLower.includes('email')) {
        value = candidate.email;
      }
      // Phone matching
      else if (labelLower.includes('phone') || 
               labelLower.includes('telephone') || 
               labelLower.includes('tel no')) {
        value = candidate.phone || candidate.mobile;
      }
      // Mobile matching
      else if (labelLower.includes('mobile') || labelLower.includes('cell')) {
        value = candidate.mobile || candidate.phone;
      }
      // Address matching
      else if (labelLower.includes('address') && !labelLower.includes('email')) {
        value = candidate.address;
      }
      // City matching
      else if (labelLower === 'city' || labelLower.includes('town')) {
        value = candidate.city;
      }
      // State matching
      else if (labelLower === 'state' || labelLower === 'county') {
        value = candidate.state;
      }
      // Zip/Postcode matching
      else if (labelLower.includes('zip') || 
               labelLower.includes('postcode') || 
               labelLower.includes('post code')) {
        value = candidate.zip;
      }
      // Date of Birth matching
      else if (labelLower.includes('date of birth') || 
               labelLower.includes('dob') || 
               labelLower.includes('birth date') ||
               labelLower.includes('d.o.b')) {
        value = candidate.dateOfBirth;
      }
      // SSN/NI Number matching  
      else if (labelLower.includes('ssn') || 
               labelLower.includes('social security') ||
               labelLower.includes('ni number') ||
               labelLower.includes('national insurance') ||
               labelLower.includes('n.i.')) {
        value = candidate.ssn;
      }
      
      // Only set if we have a value
      if (value) {
        updatedValues[field.id] = value;
      }
    });
    
    setFieldValues(updatedValues);
  }, [candidate, documentFields]);

  // Live fetch from Bullhorn using test-mapping endpoint for ALL mapped fields
  // (covers custom fields like customTextBlock1 that the hardcoded switch misses)
  useEffect(() => {
    const tid = selectedTemplate?.id;
    const bhId = candidate?.bullhorn_id;
    if (!tid || !bhId || documentFields.length === 0) return;
    
    let cancelled = false;
    (async () => {
      try {
        const response = await templatesApi.testMapping(tid, String(bhId), entityType);
        if (cancelled) return;
        const results = response.data?.results || [];
        if (results.length === 0) return;
        
        // Build original_id -> value map from test-mapping response
        const byOriginalId = {};
        results.forEach(r => {
          if (r.has_value && r.value != null) byOriginalId[r.field_id] = r.value;
        });
        
        setFieldValues(prev => {
          const next = { ...prev };
          documentFields.forEach(f => {
            const liveValue = byOriginalId[f.original_id];
            // Only overwrite if we have a live value AND the current local value is empty
            // (so any user-typed value wins)
            if (liveValue != null && (!next[f.id] || String(next[f.id]).trim() === '')) {
              next[f.id] = String(liveValue);
            }
          });
          return next;
        });
      } catch (err) {
        // Silent failure — fall back to local pre-fill
      }
    })();
    
    return () => { cancelled = true; };
  }, [selectedTemplate?.id, candidate?.bullhorn_id, documentFields.length, entityType]);

  // Live cascading filter — when a Skill field is auto-linked to a Category
  // field on this template, swap its options to only those that match the
  // currently-selected category id(s). Mirrors the candidate-side behaviour
  // so the recruiter previewing the doc sees the SAME filtered list the
  // candidate will see at completion time.
  useEffect(() => {
    if (!documentFields?.length) return;
    const dependents = documentFields.filter((f) => f.filter_by_field_id);
    if (!dependents.length) return;
    let cancelled = false;
    dependents.forEach(async (dep) => {
      const driverField = documentFields.find((f) => f.id === dep.filter_by_field_id);
      const driverValue = fieldValues[dep.filter_by_field_id];

      // Pre-filled values come through as Bullhorn labels (the flatten
      // helper returns `.name` for TO_ONE entities). Resolve back to the
      // underlying category ID so the cascade endpoint can actually
      // filter — otherwise Bullhorn ignores the param and returns the
      // full list, which looks like "cascading doesn't work".
      const labelToId = new Map();
      (driverField?.bullhorn_picklist_options || []).forEach((o) => {
        if (o?.label != null && o?.value != null) {
          labelToId.set(String(o.label).trim().toLowerCase(), String(o.value));
        }
      });
      const resolveOne = (raw) => {
        if (raw == null) return '';
        const s = String(raw).trim();
        if (!s) return '';
        if (/^\d+$/.test(s)) return s;
        return labelToId.get(s.toLowerCase()) || s;
      };
      let ids;
      if (Array.isArray(driverValue)) {
        ids = driverValue.map(resolveOne).filter(Boolean).join(',');
      } else {
        ids = resolveOne(driverValue);
      }
      try {
        const res = await axios.get(
          `${API_URL}/bullhorn/skill-options-by-category`,
          { params: { category_ids: ids }, withCredentials: true },
        );
        if (cancelled) return;
        const opts = res.data?.options || [];
        setDocumentFields((prev) => prev.map((f) =>
          f.id === dep.id
            ? {
                ...f,
                options: opts.map((o) => o.value),
                bullhorn_picklist_options: opts,
                bullhorn_picklist_multi: !!res.data?.multi,
                bullhorn_picklist_entity_backed: !!res.data?.entity_backed,
              }
            : f
        ));
        // Drop any previously-selected skill that's no longer in the
        // filtered list — but allow BOTH id matches and label matches so a
        // label-prefilled value survives the cascade if it's still valid.
        const allowedIds = new Set(opts.map((o) => String(o.value)));
        const allowedLabels = new Set(opts.map((o) => String(o.label).trim().toLowerCase()));
        const isAllowed = (v) => {
          const s = String(v ?? '').trim();
          return s !== '' && (allowedIds.has(s) || allowedLabels.has(s.toLowerCase()));
        };
        setFieldValues((prev) => {
          const cur = prev[dep.id];
          if (cur === undefined || cur === null || cur === '') return prev;
          if (Array.isArray(cur)) {
            const kept = cur.filter(isAllowed);
            return kept.length === cur.length ? prev : { ...prev, [dep.id]: kept };
          }
          return isAllowed(cur) ? prev : { ...prev, [dep.id]: '' };
        });
      } catch {
        // non-fatal — keep existing options
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(documentFields.filter(f => f.filter_by_field_id)
      .map(f => fieldValues[f.filter_by_field_id] ?? null)),
    documentFields.length,
  ]);

  const loadTemplates = async () => {
    try {
      const response = await templatesApi.list();
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const searchCandidates = async () => {
    if (searchQuery.length < 2) return;
    setSearching(true);
    try {
      // Search based on entity type
      let results = [];
      if (entityType === 'contact') {
        const response = await bullhornApi.searchContacts(searchQuery);
        // Contact search only returns firstName/lastName, not a composite
        // name — synthesize it the same way selectCandidate() does below,
        // so the dropdown list (not just the post-selection object) shows it.
        results = (response.data.contacts || []).map(c => ({
          ...c,
          name: c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim(),
        }));
      } else if (entityType === 'placement') {
        const response = await bullhornApi.searchPlacements(searchQuery);
        // Transform placement results to match expected format
        results = (response.data.placements || []).map(p => ({
          ...p,
          name: p.candidate?.name || `Placement #${p.id}`,
          email: p.candidate?.email || '',
          firstName: p.candidate?.name?.split(' ')[0] || '',
          lastName: p.candidate?.name?.split(' ').slice(1).join(' ') || '',
          jobTitle: p.jobOrder?.title || '',
          companyName: p.company?.name || '',
          // Include nested data for field mapping
          candidate: p.candidate,
          jobOrder: p.jobOrder,
          clientCorporation: p.company,
        }));
      } else {
        const response = await bullhornApi.searchCandidates(searchQuery);
        // Candidate search only returns firstName/lastName, not a composite
        // name — same fix as the contact branch above.
        results = (response.data.candidates || []).map(c => ({
          ...c,
          name: c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim(),
        }));
      }
      setSearchResults(results);
      // Friendly empty-state — recruiters mistake "0 results" for "search broken"
      if (results.length === 0) {
        const labels = { candidate: 'candidates', contact: 'contacts', placement: 'placements' };
        toast.info(`No ${labels[entityType] || entityType} matched "${searchQuery}". Try a name, partial email, or Bullhorn ID — or enter manually below.`);
      }
    } catch (error) {
      const labels = { candidate: 'candidates', contact: 'contacts', placement: 'placements' };
      toast.error(`Failed to search ${labels[entityType] || entityType}`);
    } finally {
      setSearching(false);
    }
  };

  const searchCompanies = async () => {
    if (companySearchQuery.length < 2) return;
    setSearchingCompany(true);
    try {
      const response = await bullhornApi.searchCompanies(companySearchQuery);
      setCompanySearchResults(response.data.companies || []);
    } catch (error) {
      toast.error('Failed to search companies');
    } finally {
      setSearchingCompany(false);
    }
  };

  const selectCandidate = (c) => {
    // Store all candidate/contact data from Bullhorn for field mapping
    setCandidate({
      bullhorn_id: c.id,
      name: c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim(),
      email: c.email || '',
      firstName: c.firstName || '',
      lastName: c.lastName || '',
      phone: c.phone || c.workPhone || '',
      mobile: c.mobile || c.phone2 || '',
      address: c.address?.address1 || '',
      city: c.address?.city || '',
      state: c.address?.state || '',
      zip: c.address?.zip || '',
      dateOfBirth: c.dateOfBirth ? new Date(c.dateOfBirth).toISOString().split('T')[0] : '',
      ssn: c.ssn || '',
      title: c.title || '',  // For contacts
    });

    // For placements, accumulate selections so the user can fire the same
    // document at a batch of placements in one go (a common recruiter
    // workflow — e.g. every new-start on Monday gets the same contract).
    if (entityType === 'placement') {
      // Pull both candidate AND contact email from the search result so
      // the recruiter can flip the recipient per placement at send-time.
      // `selectCandidate` is misnamed here — for placements `c` is the
      // whole placement row, with .candidate / .contact sub-objects.
      const candEmail = c.candidate?.email || c.email || '';
      const candName = c.candidate?.name || c.name || `Placement #${c.id}`;
      const contactEmail = c.contact?.email || '';
      const contactName = c.contact?.name || '';
      // Default to candidate, unless this template isn't set up to send to
      // the candidate at all (a contact-only document like "Client
      // Assignment Details") — defaulting to a side that's guaranteed to
      // be skipped is exactly what let "Both"/"Candidate" get picked for a
      // contact-only template in the first place.
      const defaultRecipientType = templateAllowsSide(selectedTemplate, 'candidate')
        ? 'candidate'
        : templateAllowsSide(selectedTemplate, 'contact') ? 'contact' : 'candidate';
      setSelectedPlacements((prev) => {
        if (prev.some((p) => String(p.id) === String(c.id))) return prev;
        return [
          ...prev,
          {
            id: c.id, // the PLACEMENT id — used for placement prefill + placement_id, NOT as the person's Bullhorn id
            name: defaultRecipientType === 'contact' ? (contactName || candName) : candName,
            email: defaultRecipientType === 'contact' ? contactEmail : candEmail,
            // Per-placement recipient choice — the recruiter can flip via
            // the chip toggle, constrained to what this template allows.
            recipient_type: defaultRecipientType,
            // Real Bullhorn person ids for write-back. These are the placement's
            // linked candidate/contact — distinct from the placement id (#25: a
            // note/file must attach to THIS candidate, never Candidate(placementId)).
            candidate_id: c.candidate?.id ?? null,
            contact_id: c.contact?.id ?? null,
            candidate_email: candEmail,
            candidate_name: candName,
            contact_email: contactEmail,
            contact_name: contactName,
            jobTitle: c.jobTitle || '',
            company_id: c.company?.id ?? c.clientCorporation?.id ?? null,
            companyName: c.companyName || '',
          },
        ];
      });
      if (c.company || c.clientCorporation) {
        const placementCompany = c.company || c.clientCorporation;
        setCompany((prev) => ({
          ...prev,
          bullhorn_id: placementCompany.id || '',
          name: placementCompany.name || c.companyName || '',
        }));
      }
    }

    // If contact has linked company, auto-populate company data
    if (entityType === 'contact' && c.company) {
      setCompany({
        bullhorn_id: c.company.id || '',
        name: c.company.name || '',
        phone: c.company.phone || '',
        address: c.company.address?.address1 || '',
        city: c.company.address?.city || '',
        state: c.company.address?.state || '',
        zip: c.company.address?.zip || '',
      });
    }

    setSearchResults([]);
    setSearchQuery('');
  };

  const removeSelectedPlacement = (placementId) => {
    setSelectedPlacements((prev) => prev.filter((p) => String(p.id) !== String(placementId)));
  };

  // Toggle a queued placement between sending to the candidate, the
  // contact, or BOTH simultaneously. For "both" the send loop fires
  // two POSTs per placement (one per recipient) — handled below.
  const setPlacementRecipientType = (placementId, recipientType) => {
    setSelectedPlacements((prev) =>
      prev.map((p) => {
        if (String(p.id) !== String(placementId)) return p;
        if (recipientType === 'both') {
          // Use the candidate's name/email as the chip's display value
          // (the send loop will explode this into two sends).
          return {
            ...p,
            recipient_type: 'both',
            name: p.candidate_name || p.name,
            email: p.candidate_email || '',
          };
        }
        const isContact = recipientType === 'contact';
        return {
          ...p,
          recipient_type: recipientType,
          name: isContact ? (p.contact_name || p.name) : (p.candidate_name || p.name),
          email: isContact ? (p.contact_email || '') : (p.candidate_email || ''),
        };
      })
    );
  };

  const selectCompany = (c) => {
    setCompany({
      bullhorn_id: c.id,
      name: c.name || '',
      phone: c.phone || '',
      address: c.address?.address1 || c.address || '',
      city: c.address?.city || '',
      state: c.address?.state || '',
      zip: c.address?.zip || '',
    });
    setCompanySearchResults([]);
    setCompanySearchQuery('');
    setShowCompanyOverride(false);
  };

  const handleSend = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }
    if (selectedTemplate.source_format === 'html' && !textStatus.ready) {
      toast.error(textStatus.blank.length
        ? `Please fill in before sending: ${textStatus.blank.join(', ')}`
        : 'Still preparing the document — try again in a moment.');
      setCurrentStep(2);
      return;
    }

    // Requester/Admin fields are mandatory before send — mirrors the
    // server-side check in createAndSend, but blocking here avoids a round
    // trip and points the sender at exactly what's missing.
    const unfilledSenderFields = (documentFields || []).filter((f) => {
      if (!f.sender_fillable) return false;
      const v = fieldValues[f.id];
      if (f.field_type === 'signature') return typeof v !== 'string' || !v.startsWith('data:image/');
      return v == null || String(v).trim() === '';
    });
    if (unfilledSenderFields.length > 0) {
      toast.error(`Please fill in before sending: ${unfilledSenderFields.map((f) => f.label).join(', ')}`);
      setCurrentStep(2);
      return;
    }

    // Placement batch mode: fire one send per selected placement. The active
    // `candidate` slot is ignored here — the user picked a set, not an
    // individual. Emails come from each placement's chosen recipient
    // (candidate, contact, or both — per the per-row toggle).
    if (entityType === 'placement' && selectedPlacements.length > 0) {
      // Build the concrete recipient list — exploding any `both` entries
      // into two sends (one to candidate, one to contact). A side this
      // template isn't set up for (e.g. the candidate side of a
      // contact-only document) is skipped here, before anything is
      // attempted — not sent-and-caught-as-a-failure. The chip toggle
      // already prevents picking a disallowed side going forward, but a
      // placement queued before the template was set (or changed) could
      // still carry a stale recipient_type.
      const expanded = [];
      const skippedSides = [];
      for (const p of selectedPlacements) {
        const rt = p.recipient_type || 'candidate';
        const sides = (rt === 'both' ? ['candidate', 'contact'] : [rt]).filter((side) => {
          if (templateAllowsSide(selectedTemplate, side)) return true;
          skippedSides.push({ id: p.id, name: p.name, side });
          return false;
        });
        for (const side of sides) {
          const email = side === 'contact' ? p.contact_email : p.candidate_email;
          const name = side === 'contact' ? (p.contact_name || p.name) : (p.candidate_name || p.name);
          expanded.push({ ...p, _send_as: side, email: email || '', name });
        }
      }

      if (expanded.length === 0) {
        toast.error(skippedSides.length
          ? `This document isn't set up to send to the ${[...new Set(skippedSides.map((s) => s.side))].join('/')}.`
          : 'Add a recipient before sending.');
        return;
      }

      const missing = expanded.filter((e) => !e.email);
      if (missing.length > 0) {
        const details = missing.map((e) => `P${e.id} (${e._send_as})`).join(', ');
        toast.error(`Missing email for ${missing.length} recipient(s): ${details}. Add the email to the linked ${missing[0]._send_as} in Bullhorn first, or flip the recipient.`);
        return;
      }

      setSending(true);
      const results = { ok: 0, failed: [] };
      try {
        for (const e of expanded) {
          try {
            await documentsApi.send({
              template_id: selectedTemplate.id,
              recipient: {
                // #25 fix: the person's Bullhorn id is the linked candidate/contact,
                // NOT the placement id (e.id). Write-back must target this person.
                bullhorn_id: (e._send_as === 'contact' ? e.contact_id : e.candidate_id) ?? null,
                name: e.name,
                email: e.email,
                entity_type: 'placement',
                placement_recipient_type: e._send_as,
                placement_id: e.id, // the placement id travels separately
                title: e.jobTitle || null,
                company_id: e.company_id ?? null,
                company_name: e.companyName || null,
              },
              company: e.company_id ? {
                bullhorn_id: e.company_id,
                name: e.companyName || null,
              } : null,
              expiry_hours: expiryHours,
              message: message || null,
              prefilled_values: fieldValues,
              custom_fields: documentFields,
            });
            results.ok += 1;
          } catch (err) {
            results.failed.push({ id: e.id, name: `${e.name} (${e._send_as})`, reason: err.response?.data?.detail });
          }
        }
        if (results.ok > 0 && results.failed.length === 0) {
          toast.success(`Sent to ${results.ok} recipient${results.ok === 1 ? '' : 's'}`);
          // Offer to log a single Bullhorn note against the FIRST placement
          // (most common case: one placement, one or two sends to it). When
          // multiple placements were queued, skip the prompt — pestering for
          // a note per placement in a row is worse than no prompt at all.
          const firstPlacement = selectedPlacements[0];
          // #25 fix: log the note against the actual person (the placement's
          // linked candidate/contact), NOT Candidate(placementId). Use the
          // primary recipient that was sent to. Skip the note if we don't have a
          // real person id — never post a note to Candidate(0)/wrong entity.
          const notePersonType = expanded[0]?._send_as === 'contact' ? 'contact' : 'candidate';
          const notePersonId = notePersonType === 'contact' ? firstPlacement?.contact_id : firstPlacement?.candidate_id;
          if (selectedPlacements.length === 1 && firstPlacement?.id && notePersonId) {
            const recipientLabel = expanded
              .map((e) => `${e.name} <${e.email}> (${e._send_as})`)
              .join(', ');
            const recipientNames = expanded.map((e) => e.name).join(', ');
            const recipientEmails = expanded.map((e) => e.email).join(', ');
            bullhornNote.fire({
              entityType: notePersonType,
              entityId: notePersonId,
              entityName: firstPlacement.name,
              documentId: null,
              context: {
                template_name: selectedTemplate.name,
                document_type: selectedTemplate.name,
                form_name: null,
                recipient_name: recipientNames || recipientLabel,
                recipient_email: recipientEmails,
                sender_name: user?.name || user?.email || '',
              },
              onComplete: () => navigate(getTenantPath('/documents?highlight=latest')),
            });
          } else {
            navigate(getTenantPath('/documents?highlight=latest'));
          }
        } else if (results.ok > 0 && results.failed.length > 0) {
          toast.warning(`Sent ${results.ok}, failed ${results.failed.length}: ${results.failed.map(f => f.name).join(', ')}`);
        } else {
          toast.error(typeof results.failed[0]?.reason === 'string' ? results.failed[0].reason : 'Failed to send to any recipient');
        }
      } finally {
        setSending(false);
      }
      return;
    }

    if (!candidate.name || !candidate.email) {
      toast.error(`Please enter ${entityType === 'contact' ? 'contact' : 'candidate'} information`);
      return;
    }

    setSending(true);
    try {
      const response = await documentsApi.send({
        template_id: selectedTemplate.id,
        recipient: {
          bullhorn_id: candidate.bullhorn_id || null,
          name: candidate.name,
          email: candidate.email,
          entity_type: entityType,
          title: candidate.title || null,
          company_id: company.bullhorn_id || null,
          company_name: company.name || null,
        },
        company: company.bullhorn_id ? {
          bullhorn_id: company.bullhorn_id,
          name: company.name,
        } : null,
        expiry_hours: expiryHours,
        message: message || null,
        prefilled_values: fieldValues,
        custom_fields: documentFields, // Send the customized fields for this document
      });

      toast.success('Document sent successfully!');
      // Offer to add a Bullhorn note against the linked record. Only makes
      // sense when the recipient is actually linked to a Bullhorn entity.
      if (candidate.bullhorn_id) {
        bullhornNote.fire({
          entityType,
          entityId: candidate.bullhorn_id,
          entityName: candidate.name,
          documentId: response?.data?.document_id || null,
          context: {
            template_name: selectedTemplate.name,
            document_type: selectedTemplate.name,
            form_name: null,
            recipient_name: candidate.name,
            recipient_email: candidate.email,
            sender_name: user?.name || user?.email || '',
          },
          onComplete: () => navigate(getTenantPath('/documents?highlight=latest')),
        });
      } else {
        navigate(getTenantPath('/documents?highlight=latest'));
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to send document';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleQuickSend = async () => {
    if (!quickSendFile) {
      toast.error('Please upload a PDF');
      return;
    }
    if (!candidate.name || !candidate.email) {
      toast.error('Please enter recipient name and email');
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('file', quickSendFile);
      formData.append('recipient_name', candidate.name);
      formData.append('recipient_email', candidate.email);
      formData.append('document_name', quickSendName || quickSendFile.name.replace('.pdf', ''));
      formData.append('entity_type', entityType);
      formData.append('expiry_hours', expiryHours.toString());
      formData.append('message', message || '');
      formData.append('requires_approval', 'false');
      formData.append('bullhorn_id', candidate.bullhorn_id || '');

      const resp = await axios.post(`${API_URL}/documents/quick-send`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document sent successfully!');
      if (candidate.bullhorn_id) {
        bullhornNote.fire({
          entityType,
          entityId: candidate.bullhorn_id,
          entityName: candidate.name,
          documentId: resp?.data?.document_id || null,
          context: {
            template_name: quickSendName || quickSendFile.name,
            document_type: quickSendName || quickSendFile.name,
            form_name: null,
            recipient_name: candidate.name,
            recipient_email: candidate.email,
            sender_name: user?.name || user?.email || '',
          },
          onComplete: () => navigate(getTenantPath('/documents?highlight=latest')),
        });
      } else {
        navigate(getTenantPath('/documents?highlight=latest'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send document');
    } finally {
      setSending(false);
    }
  };

  // Field management functions
  const addField = (type, dropPosition = null) => {
    const fieldCount = documentFields.filter(f => f.page === currentPage).length;
    const newField = {
      id: `doc_field_${Date.now()}`,
      label: `New ${FIELD_TYPES.find(f => f.value === type)?.label || type}`,
      field_type: type,
      required: false,
      position_x: dropPosition?.x || 150,
      position_y: dropPosition?.y || (100 + (fieldCount * 30)),
      width: type === 'signature' ? 180 : (type === 'checkbox' ? 20 : 150),
      height: type === 'signature' ? 45 : (type === 'checkbox' ? 20 : 24),
      page: currentPage,
      options: type === 'dropdown' ? ['Option 1', 'Option 2'] : null,
      mapped_bullhorn_field: '',
      placeholder: '',
    };
    setDocumentFields([...documentFields, newField]);
    setSelectedField(newField.id);
    setFieldValues(prev => ({ ...prev, [newField.id]: '' }));
  };

  const updateField = useCallback((fieldId, updates) => {
    setDocumentFields(prev => prev.map(f => f.id === fieldId ? { ...f, ...updates } : f));
  }, []);

  const deleteField = (fieldId) => {
    setDocumentFields(documentFields.filter(f => f.id !== fieldId));
    if (selectedField === fieldId) setSelectedField(null);
    setFieldValues(prev => {
      const newValues = { ...prev };
      delete newValues[fieldId];
      return newValues;
    });
    toast.success('Field removed');
  };

  // Drag and drop handlers
  const handleFieldTypeDragStart = (e, type) => {
    e.dataTransfer.setData('fieldType', type);
    setDraggedFieldType(type);
  };

  const handleCanvasDrop = (e) => {
    e.preventDefault();
    const fieldType = e.dataTransfer.getData('fieldType');
    if (!fieldType) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;

    addField(fieldType, { x: Math.max(0, x - 50), y: Math.max(0, y - 10) });
    setDraggedFieldType(null);
  };

  const handleCanvasDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // Keyboard delete
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedField && currentStep === 2) {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        e.preventDefault();
        deleteField(selectedField);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedField, currentStep]);

  const updateFieldValue = (fieldId, value) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const clearSenderSignaturePad = () => {
    senderSignaturePadRef.current?.clear();
  };

  const saveSenderSignature = () => {
    if (senderSignatureMode === 'type') {
      if (!senderTypedSignature.trim()) {
        toast.error('Please type your name');
        return;
      }
      const canvas = window.document.createElement('canvas');
      canvas.width = 450;
      canvas.height = 150;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 450, 150);
      ctx.fillStyle = 'black';
      ctx.font = 'italic 48px "Georgia", "Times New Roman", serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(senderTypedSignature.trim(), 225, 75);
      updateFieldValue(senderSignatureFieldId, canvas.toDataURL());
      setSenderSignatureFieldId(null);
      setSenderTypedSignature('');
      setSenderSignatureMode('draw');
    } else if (senderSignaturePadRef.current && !senderSignaturePadRef.current.isEmpty()) {
      updateFieldValue(senderSignatureFieldId, senderSignaturePadRef.current.toDataURL());
      setSenderSignatureFieldId(null);
    } else {
      toast.error('Please draw your signature first');
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // Get steps based on entity type
  const steps = getSteps(entityType);
  
  // Get Bullhorn fields based on entity type
  const getBullhornFields = () => {
    const baseFields = entityType === 'contact' ? BULLHORN_CONTACT_FIELDS : BULLHORN_CANDIDATE_FIELDS;
    if (entityType === 'contact') {
      return [...baseFields, { value: 'divider', label: '── Company Fields ──', disabled: true }, ...BULLHORN_COMPANY_FIELDS];
    }
    return baseFields;
  };

  const selectedFieldData = documentFields.find(f => f.id === selectedField);
  const currentPageFields = documentFields.filter(f => f.page === currentPage);

  const canProceed = () => {
    if (sendMode === 'quick') {
      switch (currentStep) {
        case 0: return !!quickSendFile;
        case 1: return candidate.name && candidate.email;
        default: return false;
      }
    }
    switch (currentStep) {
      case 0: return !!selectedTemplate;
      case 1:
        // Placement batch mode: having at least one queued placement is
        // sufficient — we don't require the active `candidate` slot.
        if (entityType === 'placement' && selectedPlacements.length > 0) return true;
        return candidate.name && candidate.email;
      case 2: return selectedTemplate?.source_format === 'html' ? textStatus.ready : true; // Edit fields step
      case 3: return true; // Review step
      default: return false;
    }
  };

  const quickSendSteps = [
    { id: 'upload', title: 'Upload PDF', icon: Upload },
    { id: 'recipient', title: 'Recipient', icon: User },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="send-document-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-foreground">Send Document</h1>
          <p className="text-muted-foreground font-body mt-1">
            {entityType === 'contact' ? 'Send a document to a client contact for completion'
              : entityType === 'placement' ? 'Send a document against a placement — candidate and job data flow through'
              : 'Send a document to a candidate for completion'}
          </p>
        </div>

        {/* Progress Steps */}
        <WizardSteps
          steps={sendMode === 'quick' ? quickSendSteps : steps}
          currentStep={currentStep}
        />

        {/* Step Content */}
        <div className="neumorphic rounded-xl p-8">
          {/* Step 1: Select Template or Upload */}
          {currentStep === 0 && (
            <div className="animate-fade-in" data-testid="step-template">
              {/* Mode Toggle */}
              <div className="flex border rounded-lg p-1 bg-muted/30 mb-6 max-w-md">
                <button
                  type="button"
                  onClick={() => { setSendMode('template'); setQuickSendFile(null); setQuickSendName(''); }}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    sendMode === 'template' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  From Template
                </button>
                <button
                  type="button"
                  onClick={() => { setSendMode('quick'); setSelectedTemplate(null); }}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    sendMode === 'quick' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  <Upload className="w-4 h-4" />
                  Upload PDF
                </button>
              </div>

              {sendMode === 'template' ? (
                <>
                  <h2 className="font-heading text-xl font-semibold mb-6">Select a template</h2>
                  {templates.length > 0 ? (
                    (() => {
                      // Group + alphabetise — same layout as the
                      // Templates & Packets page so recruiters scan it
                      // the same way regardless of which surface they
                      // land on.
                      const groups = { candidate: [], placement: [], contact: [] };
                      for (const t of templates) {
                        const k = (t.entity_type || 'candidate');
                        (groups[k] || groups.candidate).push(t);
                      }
                      for (const k of Object.keys(groups)) {
                        groups[k].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                      }
                      const cols = [
                        { key: 'candidate', label: 'Candidate', items: groups.candidate },
                        { key: 'placement', label: 'Placement', items: groups.placement },
                        { key: 'contact', label: 'Contact', items: groups.contact },
                      ];
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {cols.map((col) => {
                            const ec = ENTITY_COLORS[col.key] || ENTITY_COLORS.candidate;
                            return (
                              <div key={col.key} data-testid={`send-template-col-${col.key}`}>
                                <div className="flex items-center gap-2 mb-2 px-1">
                                  <span className={`inline-block w-2 h-2 rounded-full ${ec.bg}`} />
                                  <h3 className="text-sm font-semibold text-slate-700">{col.label} Documents</h3>
                                  <span className="text-xs text-muted-foreground">({col.items.length})</span>
                                </div>
                                {col.items.length === 0 ? (
                                  <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-muted-foreground">
                                    No {col.label.toLowerCase()} documents
                                  </div>
                                ) : (
                                  <div className="space-y-1.5">
                                    {col.items.map((template) => {
                                      const isSelected = selectedTemplate?.id === template.id;
                                      return (
                                        <div
                                          key={template.id}
                                          onClick={() => setSelectedTemplate(template)}
                                          className={`px-3 py-2 rounded-lg border cursor-pointer transition-all duration-150 ${
                                            isSelected ? `${ec.border} shadow-sm` : 'border-border hover:border-primary/40 bg-white'
                                          }`}
                                          style={isSelected ? { backgroundColor: col.key === 'contact' ? 'rgba(249, 168, 37, 0.05)' : col.key === 'placement' ? 'rgba(26, 74, 94, 0.05)' : 'rgba(76, 175, 80, 0.05)' } : {}}
                                          data-testid={`select-template-${template.id}`}
                                        >
                                          <div className="flex items-center gap-2">
                                            <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${isSelected ? ec.bg : ec.lightBg}`}>
                                              {col.key === 'contact' ? (
                                                <Building2 className={`w-3 h-3 ${isSelected ? 'text-white' : ec.iconColor}`} />
                                              ) : (
                                                <FileText className={`w-3 h-3 ${isSelected ? 'text-white' : ec.iconColor}`} />
                                              )}
                                            </div>
                                            <h4 className="font-medium text-sm truncate flex-1 min-w-0" title={template.name}>
                                              {template.name}
                                            </h4>
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${templateTypeTagClass(template)} flex-shrink-0 whitespace-nowrap`}>
                                              {templateTypeLabel(template)}
                                            </span>
                                            {template.is_informational && (
                                              <span
                                                className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200 flex-shrink-0 whitespace-nowrap"
                                                title="Read-only · no required fields · skips Bullhorn write-back"
                                              >
                                                <Eye className="w-2.5 h-2.5" />
                                                Info
                                              </span>
                                            )}
                                            {isSelected && (
                                              <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${ec.iconColor}`} />
                                            )}
                                          </div>
                                          <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground pl-8 whitespace-nowrap overflow-hidden">
                                            <span>{template.fields?.length || 0} fields</span>
                                            {formatEstimate(template.estimated_seconds) && (
                                              <span data-testid={`template-estimate-${template.id}`}>· {formatEstimate(template.estimated_seconds)}</span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()
                  ) : (
                    <div className="text-center py-12">
                      <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground mb-4">No templates available</p>
                      <Button onClick={() => navigate(getTenantPath('/templates'))} className="btn-secondary">
                        Create a template
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h2 className="font-heading text-xl font-semibold mb-6">Upload a PDF to send</h2>
                  <div className="max-w-lg space-y-4">
                    <div className="space-y-2">
                      <Label>Document Name</Label>
                      <Input
                        value={quickSendName}
                        onChange={(e) => setQuickSendName(e.target.value)}
                        placeholder="e.g. Employment Contract"
                        data-testid="quick-send-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Upload PDF</Label>
                      <div 
                        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                          quickSendFile ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                        }`}
                        onClick={() => document.getElementById('quick-send-file').click()}
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove('border-primary', 'bg-primary/5');
                          const f = e.dataTransfer.files[0];
                          if (f && f.name.toLowerCase().endsWith('.pdf')) {
                            setQuickSendFile(f);
                            if (!quickSendName) setQuickSendName(f.name.replace('.pdf', ''));
                          } else {
                            toast.error('Please drop a PDF file');
                          }
                        }}
                      >
                        <input
                          id="quick-send-file"
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files[0];
                            if (f) {
                              setQuickSendFile(f);
                              if (!quickSendName) setQuickSendName(f.name.replace('.pdf', ''));
                            }
                          }}
                          data-testid="quick-send-file-input"
                        />
                        {quickSendFile ? (
                          <div className="space-y-2">
                            <CheckCircle2 className="w-10 h-10 mx-auto text-primary" />
                            <p className="font-medium">{quickSendFile.name}</p>
                            <p className="text-xs text-muted-foreground">{(quickSendFile.size / 1024).toFixed(0)} KB</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <Upload className="w-10 h-10 mx-auto text-muted-foreground" />
                            <p className="text-muted-foreground">Click to upload a PDF</p>
                            <p className="text-xs text-muted-foreground">Max 10MB</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2: Choose Recipient (Candidate or Contact) */}
          {currentStep === 1 && (
            <div className="animate-fade-in" data-testid="step-recipient">
              {recipientPreFilled && candidate.name && candidate.email && (
                <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="font-medium text-emerald-800">Sending to: {candidate.name}</p>
                      <p className="text-sm text-emerald-600">{candidate.email}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setRecipientPreFilled(false); setCandidate({ name: '', email: '', bullhorn_id: '' }); }}>
                    Change
                  </Button>
                </div>
              )}
              <div className="flex items-center gap-3 mb-6">
                <h2 className="font-heading text-xl font-semibold">
                  {entityType === 'contact' ? 'Choose Contact' : entityType === 'placement' ? 'Choose Placement' : 'Choose Candidate'}
                </h2>
                <div className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${ENTITY_COLORS[entityType]?.bg} ${ENTITY_COLORS[entityType]?.text}`}>
                  {entityType === 'contact' ? <Building2 className="w-3 h-3" />
                    : entityType === 'placement' ? <Briefcase className="w-3 h-3" />
                    : <User className="w-3 h-3" />}
                  {ENTITY_COLORS[entityType]?.label}
                </div>
              </div>
              
              {/* Search from Bullhorn */}
              <div className="mb-6">
                <Label className="mb-2 block">
                  {entityType === 'placement' ? 'Search Bullhorn Placements' 
                    : entityType === 'contact' ? 'Search Bullhorn Contacts'
                    : 'Search Bullhorn Candidates'}
                </Label>
                {entityType === 'placement' && (
                  <p className="text-xs text-muted-foreground mb-2">
                    Search by candidate name, job title, company, <strong>or Placement ID</strong>. When you pick a placement, the contract will pull live data from the placement itself <em>and</em> its linked candidate, job order, and company.
                  </p>
                )}

                {/* Multi-placement send queue — accumulate placements and send
                    the same document to the whole batch in one go. */}
                {entityType === 'placement' && selectedPlacements.length > 0 && (
                  <div className="mb-3 p-3 rounded-lg bg-[#1a4a5e]/5 border border-[#1a4a5e]/20" data-testid="selected-placements-list">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-[#1a4a5e] uppercase tracking-wide flex items-center gap-1.5">
                        <Briefcase className="w-3 h-3" />
                        {selectedPlacements.length} placement{selectedPlacements.length === 1 ? '' : 's'} queued
                      </p>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-xs"
                        onClick={() => setSelectedPlacements([])}
                        data-testid="clear-placements-button"
                      >
                        Clear all
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedPlacements.map((p) => (
                        <PlacementChip
                          key={p.id}
                          placement={p}
                          onChangeRecipientType={setPlacementRecipientType}
                          onRemove={removeSelectedPlacement}
                          allowCandidate={templateAllowsSide(selectedTemplate, 'candidate')}
                          allowContact={templateAllowsSide(selectedTemplate, 'contact')}
                        />
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      Keep searching to add more placements — one document will be sent per placement when you hit <strong>Send</strong>.
                    </p>
                  </div>
                )}
                {entityType === 'candidate' && (
                  <p className="text-xs text-muted-foreground mb-2">
                    Search by name, email, or <strong>Candidate ID</strong>.
                  </p>
                )}
                {entityType === 'contact' && (
                  <p className="text-xs text-muted-foreground mb-2">
                    Search by name, email, or <strong>Contact ID</strong>.
                  </p>
                )}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchCandidates()}
                      placeholder={entityType === 'placement'
                        ? 'Search by candidate name, job title, company, or Placement ID…'
                        : entityType === 'contact'
                        ? 'Search by contact name, email, or Contact ID…'
                        : 'Search by name, email, or Candidate ID…'}
                      className={`pl-10 input-focus ${ENTITY_COLORS[entityType]?.border}`}
                      data-testid="recipient-search-input"
                    />
                  </div>
                  <Button 
                    onClick={searchCandidates}
                    disabled={searching || searchQuery.length < 2}
                    className="btn-secondary"
                    data-testid="search-button"
                  >
                    {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Search'}
                  </Button>
                </div>
                
                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="mt-2 border border-border rounded-lg overflow-hidden">
                    {searchResults.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => selectCandidate(c)}
                        className="w-full flex items-center gap-3 p-3 hover:bg-muted transition-colors duration-200 text-left"
                        data-testid={`result-${c.id}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${ENTITY_COLORS[entityType]?.lightBg}`}>
                          <span className={`font-heading text-sm font-semibold ${ENTITY_COLORS[entityType]?.border?.replace('border-', 'text-')}`}>
                            {c.name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm flex items-center gap-2 flex-wrap">
                            <span>{c.name}</span>
                            <span className="text-xs font-mono text-muted-foreground bg-slate-100 px-1.5 py-0.5 rounded">
                              {entityType === 'placement' ? 'P' : entityType === 'contact' ? 'C' : '#'}{c.id}
                            </span>
                          </p>
                          {c.email && <p className="text-xs text-muted-foreground">{c.email}</p>}
                          {/* Contact with company */}
                          {entityType === 'contact' && c.company?.name && (
                            <p className="text-xs text-purple-600 flex items-center gap-1 mt-0.5">
                              <Building2 className="w-3 h-3" />
                              {c.company.name}
                            </p>
                          )}
                          {/* Placement with job title and company */}
                          {entityType === 'placement' && (c.jobTitle || c.companyName) && (
                            <p className="text-xs text-[#1a4a5e] flex items-center gap-1 mt-0.5">
                              <Building2 className="w-3 h-3" />
                              {c.jobTitle}{c.jobTitle && c.companyName ? ' @ ' : ''}{c.companyName}
                            </p>
                          )}
                        </div>
                        {entityType === 'contact' && c.title && (
                          <span className="text-xs text-muted-foreground">{c.title}</span>
                        )}
                        {entityType === 'placement' && c.status && (
                          <span className="text-xs px-2 py-0.5 bg-slate-100 rounded-full text-slate-600">{c.status}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-border" />
                <span className="text-sm text-muted-foreground">or enter manually</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Manual Entry */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    value={candidate.name}
                    onChange={(e) => setCandidate({ ...candidate, name: e.target.value })}
                    placeholder="John Doe"
                    className="input-focus"
                    data-testid="recipient-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={candidate.email}
                    onChange={(e) => setCandidate({ ...candidate, email: e.target.value })}
                    placeholder="john@example.com"
                    className="input-focus"
                    data-testid="recipient-email-input"
                  />
                </div>
                
                {/* Company section for Contacts */}
                {entityType === 'contact' && (
                  <div className="mt-6 p-4 rounded-lg bg-[#F9A825]/5 border border-[#F9A825]/20">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-[#F9A825]" />
                        <Label className="text-[#F9A825] font-medium">Company</Label>
                      </div>
                      {company.name && !showCompanyOverride && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setShowCompanyOverride(true)}
                          className="text-xs text-muted-foreground hover:text-[#F9A825]"
                        >
                          Change company
                        </Button>
                      )}
                    </div>
                    
                    {company.name && !showCompanyOverride ? (
                      <div className="flex items-center gap-3 p-2 bg-white rounded-lg border border-[#F9A825]/20">
                        <div className="w-8 h-8 rounded-lg bg-[#F9A825]/10 flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-[#F9A825]" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{company.name}</p>
                          {company.address && <p className="text-xs text-muted-foreground">{company.address}</p>}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              value={companySearchQuery}
                              onChange={(e) => setCompanySearchQuery(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && searchCompanies()}
                              placeholder="Search company name"
                              className="pl-10 input-focus border-[#F9A825]/30"
                              data-testid="company-search-input"
                            />
                          </div>
                          <Button 
                            onClick={searchCompanies}
                            disabled={searchingCompany || companySearchQuery.length < 2}
                            variant="outline"
                            className="border-[#F9A825]/30 hover:bg-[#F9A825]/10"
                          >
                            {searchingCompany ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Search'}
                          </Button>
                        </div>
                        
                        {companySearchResults.length > 0 && (
                          <div className="border border-[#F9A825]/20 rounded-lg overflow-hidden bg-white">
                            {companySearchResults.map((c) => (
                              <button
                                key={c.id}
                                onClick={() => selectCompany(c)}
                                className="w-full flex items-center gap-3 p-3 hover:bg-[#F9A825]/5 transition-colors text-left"
                                data-testid={`company-result-${c.id}`}
                              >
                                <Building2 className="w-4 h-4 text-[#F9A825]" />
                                <div>
                                  <p className="font-medium text-sm">{c.name}</p>
                                  {c.address && <p className="text-xs text-muted-foreground">{c.address}</p>}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {showCompanyOverride && company.name && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setShowCompanyOverride(false)}
                            className="text-xs"
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Customize Document - Full Editor */}
          {currentStep === 2 && selectedTemplate?.source_format === 'html' && (
            <TextTemplateFillIn
              template={selectedTemplate}
              documentFields={documentFields}
              fieldValues={fieldValues}
              setFieldValues={setFieldValues}
              placement={selectedPlacements[0] || null}
              candidate={candidate}
              company={company}
              onStatus={setTextStatus}
            />
          )}

          {currentStep === 2 && selectedTemplate?.source_format !== 'html' && (
            <div className="animate-fade-in -mx-4 sm:-mx-6 lg:-mx-8" data-testid="step-edit-fields">
              <div className="px-4 sm:px-6 lg:px-8 mb-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <h2 className="font-heading text-xl font-semibold truncate">
                      Customize for {candidate.name}
                    </h2>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {selectedTemplate?.name && (
                        <span
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-slate-100 border border-slate-200 px-2 py-1 rounded-md"
                          data-testid="customize-template-pill"
                          title="The template being customized for this send"
                        >
                          <FileText className="w-3.5 h-3.5 text-slate-500" />
                          {selectedTemplate.name}
                        </span>
                      )}
                      <p className="text-sm text-muted-foreground">
                        Add, remove, or reposition fields. Pre-fill values before sending.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">{documentFields.length} fields</span>
                      <span className="text-green-600 font-medium">
                        ({Object.values(fieldValues).filter(v => v && v.toString().trim() !== '').length} pre-filled)
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">Press Delete to remove</span>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-12 gap-4 px-4 sm:px-6 lg:px-8" style={{ minHeight: '600px' }}>
                {/* Left: Field Types (Add Fields) */}
                <div className="col-span-2 space-y-2">
                  <div className="neumorphic rounded-lg p-3">
                    <h3 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Add Fields</h3>
                    <p className="text-xs text-muted-foreground mb-3">Drag to canvas</p>
                    <div className="space-y-1.5">
                      {FIELD_TYPES.map(({ value, label, icon: Icon }) => (
                        <button
                          key={value}
                          onClick={() => addField(value)}
                          draggable
                          onDragStart={(e) => handleFieldTypeDragStart(e, value)}
                          onDragEnd={() => setDraggedFieldType(null)}
                          className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all text-left text-xs border cursor-grab active:cursor-grabbing ${FIELD_COLORS[value]} active:scale-95 hover:shadow-md`}
                          data-testid={`add-${value}-field`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          <span className="font-medium">{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Center: PDF Canvas with Fields */}
                <div className="col-span-7">
                  <div className="neumorphic rounded-lg p-3 h-full flex flex-col">
                    {/* Toolbar */}
                    <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setScale(Math.max(0.5, scale - 0.1))}>
                          <ZoomOut className="w-4 h-4" />
                        </Button>
                        <span className="text-xs text-muted-foreground w-12 text-center">{Math.round(scale * 100)}%</span>
                        <Button variant="ghost" size="sm" onClick={() => setScale(Math.min(1.2, scale + 0.1))}>
                          <ZoomIn className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={fitToWidth} title="Fit document width" data-testid="fit-to-width-btn" className="text-xs px-2">
                          Fit
                        </Button>
                      </div>
                      {numPages > 1 && (
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage <= 1}>
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <span className="text-xs">Page {currentPage} / {numPages}</span>
                          <Button variant="ghost" size="sm" onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))} disabled={currentPage >= numPages}>
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* PDF Canvas */}
                    <div ref={pdfScrollRef} className="flex-1 overflow-auto bg-gray-100 rounded-lg">
                      <div
                        ref={canvasRef}
                        className={`relative bg-white mx-auto shadow-lg transition-all ${draggedFieldType ? 'ring-2 ring-[#f97316] ring-dashed' : ''}`}
                        style={{ width: pdfInfo.width * scale, height: pdfInfo.height * scale, minHeight: 500 * scale }}
                        onClick={() => setSelectedField(null)}
                        onDrop={handleCanvasDrop}
                        onDragOver={handleCanvasDragOver}
                      >
                        {draggedFieldType && (
                          <div className="absolute inset-0 bg-[#f97316]/5 pointer-events-none z-30 flex items-center justify-center">
                            <div className="bg-white/90 px-4 py-2 rounded-lg shadow-lg border border-[#f97316]">
                              <p className="text-sm font-medium text-[#f97316]">Drop to place field</p>
                            </div>
                          </div>
                        )}
                        
                        {selectedTemplate?.file_url ? (
                          <Document
                            file={`${BACKEND_URL}${selectedTemplate.file_url}`}
                            options={PDF_HTTP_OPTIONS}
                            onLoadSuccess={onDocumentLoadSuccess}
                            loading={<div className="flex items-center justify-center h-full"><RefreshCw className="w-6 h-6 animate-spin text-[#f97316]" /></div>}
                          >
                            <Page pageNumber={currentPage} scale={scale} renderTextLayer={false} renderAnnotationLayer={false} />
                          </Document>
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground border-2 border-dashed border-border rounded-lg m-4">
                            <div className="text-center">
                              <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                              <p className="text-sm">No PDF in this template</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Draggable Fields */}
                        {currentPageFields.map((field) => (
                          <DraggableFieldMini
                            key={field.id}
                            field={field}
                            isSelected={selectedField === field.id}
                            scale={scale}
                            onSelect={setSelectedField}
                            onUpdate={updateField}
                            onDelete={deleteField}
                            fieldValue={getFieldPreviewValue(field, fieldValues[field.id])}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: Field Properties & Values */}
                <div className="col-span-3 space-y-3">
                  {/* Field Properties */}
                  <div className="neumorphic rounded-lg p-3 max-h-[450px] overflow-auto">
                    <h3 className="text-xs font-semibold uppercase text-muted-foreground mb-3">
                      {selectedFieldData ? 'Field Properties' : 'Select a Field'}
                    </h3>
                    
                    {selectedFieldData ? (
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Label</Label>
                          <Input
                            value={selectedFieldData.label}
                            onChange={(e) => updateField(selectedField, { label: e.target.value })}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Type</Label>
                          <Select value={selectedFieldData.field_type} onValueChange={(v) => updateField(selectedField, { field_type: v })}>
                            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Bullhorn Integration Section */}
                        <div className="pt-2 border-t border-border">
                          <div className="flex items-center gap-1 mb-2">
                            <Database className="w-3 h-3 text-blue-500" />
                            <span className="text-xs font-semibold text-blue-600">Bullhorn Integration</span>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="space-y-1">
                              <Label className="text-xs">Source from Bullhorn</Label>
                              <Select 
                                value={selectedFieldData.bullhorn_source_field || 'none'} 
                                onValueChange={(v) => updateField(selectedField, { bullhorn_source_field: v === 'none' ? '' : v })}
                              >
                                <SelectTrigger className="h-8 text-sm">
                                  <SelectValue placeholder="Select Bullhorn field" />
                                </SelectTrigger>
                                <SelectContent>
                                  {getBullhornFields().map(f => (
                                    <SelectItem 
                                      key={f.value} 
                                      value={f.value}
                                      disabled={f.disabled}
                                      className={f.disabled ? 'text-muted-foreground font-medium' : ''}
                                    >
                                      {f.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id="field-write-back"
                                checked={selectedFieldData.write_back_to_bullhorn || false}
                                onCheckedChange={(c) => updateField(selectedField, { write_back_to_bullhorn: c })}
                              />
                              <Label htmlFor="field-write-back" className="text-xs flex items-center gap-1">
                                <ArrowUpDown className="w-3 h-3" />
                                Write back to Bullhorn
                              </Label>
                            </div>
                          </div>
                        </div>

                        {/* Recipient Access Section */}
                        <div className="pt-2 border-t border-border">
                          <div className="flex items-center gap-1 mb-2">
                            {entityType === 'contact' ? (
                              <Building2 className="w-3 h-3 text-[#F9A825]" />
                            ) : (
                              <User className="w-3 h-3 text-[#4CAF50]" />
                            )}
                            <span className={`text-xs font-semibold ${entityType === 'contact' ? 'text-[#F9A825]' : 'text-[#4CAF50]'}`}>
                              {entityType === 'contact' ? 'Contact' : 'Candidate'} Access
                            </span>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id="field-required"
                                checked={selectedFieldData.required}
                                onCheckedChange={(c) => updateField(selectedField, { required: c })}
                              />
                              <Label htmlFor="field-required" className="text-xs">Required field</Label>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id="field-editable"
                                checked={selectedFieldData.candidate_editable !== false}
                                onCheckedChange={(c) => updateField(selectedField, { candidate_editable: c })}
                              />
                              <Label htmlFor="field-editable" className="text-xs flex items-center gap-1">
                                {selectedFieldData.candidate_editable !== false ? (
                                  <Unlock className="w-3 h-3 text-green-500" />
                                ) : (
                                  <Lock className="w-3 h-3 text-red-500" />
                                )}
                                Candidate can edit
                              </Label>
                            </div>
                            
                            {selectedFieldData.candidate_editable === false && (
                              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                                This field will be shown as read-only to the candidate
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Size Controls */}
                        <div className="pt-2 border-t border-border">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">Width</Label>
                              <Input type="number" value={Math.round(selectedFieldData.width)} onChange={(e) => updateField(selectedField, { width: parseInt(e.target.value) || 100 })} className="h-8 text-sm" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Height</Label>
                              <Input type="number" value={Math.round(selectedFieldData.height)} onChange={(e) => updateField(selectedField, { height: parseInt(e.target.value) || 24 })} className="h-8 text-sm" />
                            </div>
                          </div>
                        </div>
                        
                        <Button variant="destructive" size="sm" className="w-full" onClick={() => deleteField(selectedField)}>
                          <Trash2 className="w-3 h-3 mr-2" /> Remove Field
                        </Button>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">Click a field on the document to edit its properties</p>
                    )}
                  </div>

                  {/* Pre-fill Values */}
                  <div className="neumorphic rounded-lg p-3 max-h-[300px] overflow-auto">
                    <h3 className="text-xs font-semibold uppercase text-muted-foreground mb-3">Pre-fill Values</h3>
                    <div className="space-y-2">
                      {documentFields.length > 0 ? documentFields.map((field) => (
                        <div key={field.id} className={`space-y-1 p-2 rounded ${field.sender_fillable ? 'bg-violet-50 border border-violet-200' : field.candidate_editable === false ? 'bg-amber-50 border border-amber-200' : ''}`}>
                          <Label className="text-xs flex items-center gap-1">
                            {field.sender_fillable ? (
                              <ShieldCheck className="w-3 h-3 text-violet-600" />
                            ) : field.candidate_editable === false ? (
                              <Lock className="w-3 h-3 text-amber-600" />
                            ) : null}
                            {field.bullhorn_source_field && <Database className="w-3 h-3 text-blue-500" />}
                            {field.label}
                            {(field.required || field.sender_fillable) && <span className="text-red-500">*</span>}
                            {field.write_back_to_bullhorn && <ArrowUpDown className="w-3 h-3 text-green-500" title="Will sync to Bullhorn" />}
                          </Label>
                          {field.field_type === 'signature' ? (
                            field.sender_fillable ? (
                              fieldValues[field.id] ? (
                                <div className="flex items-center gap-2 p-1.5 border border-violet-200 rounded bg-white">
                                  <img src={fieldValues[field.id]} alt="Your signature" className="h-8 max-w-[120px] object-contain" />
                                  <Button type="button" size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={() => setSenderSignatureFieldId(field.id)}>
                                    Re-sign
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="w-full h-7 text-xs gap-1.5 border-violet-300 text-violet-700 hover:bg-violet-100"
                                  onClick={() => setSenderSignatureFieldId(field.id)}
                                  data-testid={`sign-now-${field.id}`}
                                >
                                  <PenTool className="w-3 h-3" /> Sign now
                                </Button>
                              )
                            ) : (
                              <div className="p-2 border border-dashed border-border rounded text-center text-xs text-muted-foreground bg-rose-50">
                                Candidate signs
                              </div>
                            )
                          ) : field.field_type === 'checkbox' ? (
                            <div className="flex items-center gap-2">
                              <Checkbox checked={fieldValues[field.id] === 'true'} onCheckedChange={(c) => updateFieldValue(field.id, c ? 'true' : 'false')} />
                              <span className="text-xs text-muted-foreground">Pre-check</span>
                            </div>
                          ) : (
                            <div className="relative">
                              <Input
                                type={
                                  field.field_type === 'date' ? 'date'
                                    : field.field_type === 'email' ? 'email'
                                    : field.field_type === 'number' ? 'number'
                                    : field.field_type === 'phone' ? 'tel'
                                    : 'text'
                                }
                                value={getFieldDisplayValue(field, fieldValues[field.id])}
                                onChange={(e) => updateFieldValue(field.id, e.target.value)}
                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                className="h-7 text-xs"
                              />
                              {field.sender_fillable ? (
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-violet-600 font-medium">
                                  REQUIRED
                                </span>
                              ) : field.candidate_editable === false && (
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-amber-600 font-medium">
                                  LOCKED
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )) : (
                        <p className="text-xs text-muted-foreground">No fields to pre-fill</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review & Send */}
          {currentStep === 3 && (
            <div className="animate-fade-in" data-testid="step-review">
              <h2 className="font-heading text-xl font-semibold mb-6">Review & Send</h2>
              
              <div className="space-y-6">
                {/* Summary */}
                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Template</span>
                    <span className="font-medium">{selectedTemplate?.name}</span>
                  </div>
                  {entityType === 'placement' && selectedPlacements.length > 0 ? (
                    selectedPlacements.length === 1 ? (
                      (() => {
                        // Mirror handleSend's own recipient resolution so
                        // this never shows something different from what
                        // actually goes out — the per-placement chip
                        // (Candidate/Contact/Both) is the source of truth,
                        // not the generic `candidate` prefill state (which
                        // is set once at search-select time from the
                        // placement's candidate, before the chip exists).
                        // A side this template isn't set up for (e.g. the
                        // candidate side of a contact-only document) is
                        // filtered out here too — "Both" only ever means
                        // "both sides this document actually goes to".
                        const p = selectedPlacements[0];
                        const rt = p.recipient_type || 'candidate';
                        const sides = (rt === 'both' ? ['candidate', 'contact'] : [rt])
                          .filter((side) => templateAllowsSide(selectedTemplate, side));
                        if (sides.length === 0) {
                          return (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Recipient</span>
                              <span className="font-medium text-destructive">Not set up for this template</span>
                            </div>
                          );
                        }
                        const label = sides.length === 2 ? 'Recipients' : sides[0] === 'contact' ? 'Contact' : 'Candidate';
                        const name = sides.length === 2
                          ? `${p.candidate_name || 'Candidate'} + ${p.contact_name || 'Contact'}`
                          : sides[0] === 'contact' ? (p.contact_name || p.name) : (p.candidate_name || p.name);
                        const email = sides.length === 2
                          ? `${p.candidate_email || '—'} + ${p.contact_email || '—'}`
                          : sides[0] === 'contact' ? p.contact_email : p.candidate_email;
                        return (
                          <>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">{label}</span>
                              <span className="font-medium">{name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Email</span>
                              <span className="font-medium">{email}</span>
                            </div>
                          </>
                        );
                      })()
                    ) : (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Recipients</span>
                        <span className="font-medium">{selectedPlacements.length} placements queued</span>
                      </div>
                    )
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{entityType === 'contact' ? 'Contact' : 'Candidate'}</span>
                        <span className="font-medium">{candidate.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Email</span>
                        <span className="font-medium">{candidate.email}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Document Fields</span>
                    <span className="font-medium">{documentFields.length} fields</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Pre-filled Values</span>
                    <span className="font-medium">
                      {Object.values(fieldValues).filter(v => v && v !== '').length} of {documentFields.length}
                    </span>
                  </div>
                </div>

                {/* Expiry */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Link Expiry
                  </Label>
                  <Select 
                    value={expiryHours.toString()}
                    onValueChange={(v) => setExpiryHours(parseInt(v))}
                  >
                    <SelectTrigger data-testid="expiry-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {linkExpiryOptions(expiryHours).map((o) => (
                        <SelectItem key={o.hours} value={String(o.hours)}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Message */}
                <div className="space-y-2">
                  <Label>Personal Message (optional)</Label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Add a personal message to include in the email..."
                    className="input-focus"
                    rows={3}
                    data-testid="message-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-border">
            <Button
              variant="ghost"
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              data-testid="previous-step-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
            
            {sendMode === 'quick' && currentStep === 1 ? (
              <Button
                onClick={handleQuickSend}
                disabled={sending || !canProceed()}
                className="btn-primary"
                data-testid="quick-send-button"
              >
                {sending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Document
                  </>
                )}
              </Button>
            ) : currentStep < (sendMode === 'quick' ? quickSendSteps : steps).length - 1 ? (
              <Button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!canProceed()}
                className="btn-primary"
                data-testid="next-step-button"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSend}
                disabled={sending || !canProceed()}
                className="btn-primary"
                data-testid="send-document-button"
              >
                {sending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {entityType === 'placement' && selectedPlacements.length > 1
                      ? `Send to ${selectedPlacements.length} Placements`
                      : 'Send Document'}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Sender/admin signature capture — for a Requester/Admin signature field */}
      <Dialog open={!!senderSignatureFieldId} onOpenChange={(open) => !open && setSenderSignatureFieldId(null)}>
        <DialogContent className="max-w-md" data-testid="sender-signature-dialog">
          <DialogHeader>
            <DialogTitle>Sign document</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2 mb-3">
            <Button
              type="button"
              size="sm"
              variant={senderSignatureMode === 'draw' ? 'default' : 'outline'}
              onClick={() => setSenderSignatureMode('draw')}
            >
              Draw
            </Button>
            <Button
              type="button"
              size="sm"
              variant={senderSignatureMode === 'type' ? 'default' : 'outline'}
              onClick={() => setSenderSignatureMode('type')}
            >
              Type
            </Button>
          </div>
          {senderSignatureMode === 'draw' ? (
            <div className="space-y-2">
              <canvas
                ref={initSenderSignatureCanvas}
                width={420}
                height={150}
                className="w-full border border-border rounded-lg bg-white touch-none"
                data-testid="sender-signature-canvas"
              />
              <Button type="button" size="sm" variant="ghost" onClick={clearSenderSignaturePad}>
                Clear
              </Button>
            </div>
          ) : (
            <Input
              value={senderTypedSignature}
              onChange={(e) => setSenderTypedSignature(e.target.value)}
              placeholder="Type your full name"
              className="italic"
              style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
              data-testid="sender-signature-type-input"
              autoFocus
            />
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSenderSignatureFieldId(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={saveSenderSignature} data-testid="sender-signature-save-btn">
              Save signature
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BullhornNotePromptDialog {...bullhornNote.popup} />
    </DashboardLayout>
  );
}
